<script setup lang="ts">
/**
 * Каркас экрана: сайдбар 240px и колонка контента. Тулбар рисует сам экран —
 * в макете у него десять контекстов, и общий на всех оказался бы набором
 * условий вместо разметки.
 *
 * Высота каркаса — ровно экран, и прокручивается только колонка контента
 * (DEV-1587). При `min-height: 100vh` прокручивалась страница целиком, и на
 * длинном списке задач вместе с ней уезжали и сайдбар, и тулбар: навигация
 * пропадала ровно там, где она нужнее всего.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { cdnUrl } from '@/api/cdn'
import CommandPalette from '@/components/CommandPalette.vue'
import MobileTabBar from '@/components/MobileTabBar.vue'
import TrackerSidebar from '@/components/TrackerSidebar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import AssistantPanel from '@/features/assistant/AssistantPanel.vue'
import { useAssistantContext } from '@/features/assistant/useAssistantContext'
import { useAuthStore } from '@/features/auth/store'
import { useProjectsStore } from '@/features/projects/store'
import { useAssistantStore } from '@/stores/assistant'

const COLLAPSED_KEY = 'tracker-sidebar-collapsed'

const router = useRouter()

const auth = useAuthStore()
const projects = useProjectsStore()
const assistant = useAssistantStore()

/** Ассистент есть только у тех, кому его открыли: без права точки входа нет. */
const canAssist = computed(() => auth.can('tracker:ai'))

// Контекст едет за навигацией: спрашивают про ту страницу, которая открыта, а
// не про ту, с которой начали разговор. Композабл считает дескриптор сам —
// каркасу остаётся передать его в стор, откуда его берут и чип, и ход.
const page = useAssistantContext()

watch(page, (next) => assistant.setPage(next), { immediate: true })

// Проекты нужны сайдбару на любом экране, а не только в хабе: без них у
// навигации нет ни одного проектного пункта.
onMounted(() => {
  void projects.ensureLoaded()
})

const isMobile = useIsMobile()

const collapsed = ref(localStorage.getItem(COLLAPSED_KEY) === '1')

const user = computed(() =>
  auth.user
    ? {
        name: auth.user.name || auth.user.email,
        role: auth.user.roles[0],
        avatar: cdnUrl(auth.avatarFile),
      }
    : null,
)

const logout = async () => {
  await auth.logout()
  await router.push({ name: 'login' })
}

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value
  localStorage.setItem(COLLAPSED_KEY, collapsed.value ? '1' : '0')
}
</script>

<template>
  <div class="layout">
    <TrackerSidebar
      v-if="!isMobile"
      :collapsed="collapsed"
      :user="user"
      :assistant="canAssist"
      :assistant-open="assistant.isOpen"
      @toggle-collapse="toggleCollapsed"
      @toggle-assistant="assistant.toggle()"
      @logout="logout"
      @add-project="router.push({ name: 'projects', query: { create: '1' } })"
    />

    <main class="layout__main" :class="{ 'layout__main--mobile': isMobile }">
      <RouterView />
    </main>

    <!--
      Панель — сосед контента, а не оверлей: `main` сжимается, и доска
      переверстывается сама. На телефоне места на две колонки нет, там
      ассистент будет листом (макет 8-12, DEV-1376).
    -->
    <AssistantPanel v-if="assistant.isOpen && canAssist && !isMobile" />

    <!-- На телефоне сайдбара нет: навигация уезжает вниз (макет 27:2). -->
    <MobileTabBar v-if="isMobile" />

    <!-- Палитра живёт в каркасе: Cmd+K обязан работать на любом экране. -->
    <CommandPalette />
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: var(--color-bg-app);
}

.layout__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
}

/* Место под таб-бар: без отступа последняя строка списка прячется под ним. */
.layout__main--mobile {
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px));
}
</style>
